import React, { useEffect, useState } from 'react';
import { Table, message, Select, Row, Col, Button, Space, Card } from 'antd';
import { FilterOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../Config/axiosConfig';
import getClassPublishedColumns from '../../Config/getClassPublishedColumns';
import UpdateClassModal from '../UpdateClassModal/UpdateClassModal';

const { Option } = Select;

function Published() {
  const [allClasses, setAllClasses] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [educationLevels, setEducationLevels] = useState([]);
  const [allEducationClasses, setAllEducationClasses] = useState([]);
  const [filters, setFilters] = useState({
    subjectId: null,
    educationLevelId: null,
    educationClass: null,
  });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const navigate = useNavigate();

  // Subject, education level, and class level are all supported directly by
  // the API, so they're sent as request filters and applied server-side.
  const fetchClasses = async (queryFilters = {}) => {
    setLoading(true);
    try {
      const basePayload = {};
      if (queryFilters.subjectId) basePayload.subjectId = queryFilters.subjectId;
      if (queryFilters.educationLevelId) basePayload.educationClassLevel = queryFilters.educationLevelId;
      if (queryFilters.educationClass) basePayload.educationClass = queryFilters.educationClass;

      const firstResponse = await axiosInstance.post('/Public/get-paged-classes', { ...basePayload, pageNumber: 1 });
      const firstData = firstResponse?.data?.data;

      if (!firstData || !Array.isArray(firstData.classes)) {
        console.error('Invalid data structure:', firstData);
        message.error('No class data received.');
        setAllClasses([]);
        setClasses([]);
        return;
      }

      let combined = firstData.classes;
      const totalPages = firstData.pages || 1;

      if (totalPages > 1) {
        const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
        const remainingResponses = await Promise.all(
          remainingPages.map(pageNumber =>
            axiosInstance.post('/Public/get-paged-classes', { ...basePayload, pageNumber })
          )
        );
        remainingResponses.forEach(res => {
          const pageClasses = res?.data?.data?.classes;
          if (Array.isArray(pageClasses)) {
            combined = combined.concat(pageClasses);
          }
        });
      }

      setAllClasses(combined);
      setClasses(combined);
    } catch (error) {
      console.error('Error fetching classes:', error);
      console.error('Error details:', error.response?.data || error.message);
      message.error('Error fetching classes');
      setAllClasses([]);
      setClasses([]);
    } finally {
      setLoading(false);
    }
  };

  // Lookups for the filter dropdowns, loaded once
  const fetchFilterOptions = async () => {
    try {
      const lookupsResponse = await axiosInstance.post('/Public/get-lookups');

      if (lookupsResponse.data?.code === 200) {
        setSubjects(lookupsResponse.data.data.subjects || []);
        setEducationLevels(lookupsResponse.data.data.educationLevels || []);
        setAllEducationClasses(lookupsResponse.data.data.educationClasses || []);
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  useEffect(() => {
    fetchClasses();
    fetchFilterOptions();
  }, []);

  // Class level options depend on which education level is selected
  // (e.g. Standard 1-7 under Primary, Form 1-4 under Secondary)
  const educationClasses = filters.educationLevelId
    ? allEducationClasses.filter(cls => cls.education_level_id === filters.educationLevelId)
    : [];

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleLevelChange = (value) => {
    setFilters(prev => ({ ...prev, educationLevelId: value, educationClass: null }));
  };

  const applyFilters = () => {
    fetchClasses(filters);
  };

  const handleResetFilters = () => {
    const clearedFilters = { subjectId: null, educationLevelId: null, educationClass: null };
    setFilters(clearedFilters);
    fetchClasses(clearedFilters);
  };

  const handleRowClick = (record) => {
    navigate(`/preview-class/${record.id}`);
  };

  const handleEditClass = (record) => {
    setSelectedClass(record);
    setIsModalVisible(true);
  };

  const handleUpdateSuccess = () => {
    fetchClasses(filters);
  };

  const columns = getClassPublishedColumns(
    navigate,
    () => fetchClasses(filters),
    1,
    handleEditClass
  );

  const hasActiveFilters = filters.subjectId || filters.educationLevelId || filters.educationClass;

  return (
    <div className="student-admin-container">
      <Card
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0 }}>Published Classes</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '12px', color: '#666' }}>
                {allClasses.length} classes loaded
              </span>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => fetchClasses(filters)}
                loading={loading}
                size="small"
              >
                Refresh
              </Button>
            </div>
          </div>
        }
      >
        {/* Filters Section */}
        <div style={{ marginBottom: 20 }}>
          <Row gutter={[16, 16]} align="middle">
            {/* Education Level Filter */}
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Filter by Education Level"
                style={{ width: '100%' }}
                value={filters.educationLevelId}
                onChange={handleLevelChange}
                allowClear
                disabled={educationLevels.length === 0}
              >
                {educationLevels.map(level => (
                  <Option key={level.id} value={level.id}>
                    {level.name}
                  </Option>
                ))}
              </Select>
            </Col>

            {/* Class Level Filter (e.g. Form 1, Standard 1) */}
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder={filters.educationLevelId ? 'Filter by Class Level' : 'Select Education Level First'}
                style={{ width: '100%' }}
                value={filters.educationClass}
                onChange={(value) => handleFilterChange('educationClass', value)}
                allowClear
                disabled={!filters.educationLevelId || educationClasses.length === 0}
              >
                {educationClasses.map(cls => (
                  <Option key={cls.id} value={cls.id}>
                    {cls.name}
                  </Option>
                ))}
              </Select>
            </Col>

            {/* Subject Filter */}
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Filter by Subject"
                style={{ width: '100%' }}
                value={filters.subjectId}
                onChange={(value) => handleFilterChange('subjectId', value)}
                allowClear
                disabled={subjects.length === 0}
              >
                {subjects.map(subject => (
                  <Option key={subject.id} value={subject.id}>
                    {subject.name}
                  </Option>
                ))}
              </Select>
            </Col>

            {/* Filter Actions */}
            <Col xs={24} sm={12} md={6}>
              <Space>
                <Button
                  onClick={applyFilters}
                  icon={<FilterOutlined />}
                  type="primary"
                >
                  Apply Filters
                </Button>
                <Button
                  onClick={handleResetFilters}
                  disabled={!hasActiveFilters}
                >
                  Clear Filters
                </Button>
              </Space>
            </Col>
          </Row>

          {/* Filter Summary */}
          {hasActiveFilters && (
            <div style={{ marginTop: 16, padding: 8, backgroundColor: '#f6ffed', borderRadius: 4 }}>
              <span style={{ marginRight: 8 }}>Active Filters:</span>
              {filters.educationLevelId && (
                <span style={{ marginRight: 16 }}>
                  Education Level: <strong>{educationLevels.find(l => l.id === filters.educationLevelId)?.name}</strong>
                </span>
              )}
              {filters.educationClass && (
                <span style={{ marginRight: 16 }}>
                  Class Level: <strong>{allEducationClasses.find(c => c.id === filters.educationClass)?.name}</strong>
                </span>
              )}
              {filters.subjectId && (
                <span>
                  Subject: <strong>{subjects.find(s => s.id === filters.subjectId)?.name}</strong>
                </span>
              )}
            </div>
          )}
        </div>

        {/* Results Count */}
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            Showing {classes.length} of {allClasses.length} total classes
          </div>
        </div>

        {/* Main Table */}
        <Table
          columns={columns}
          dataSource={classes}
          rowKey="id"
          loading={loading}
          pagination={{
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} classes`,
            showLessItems: false,
            hideOnSinglePage: false,
          }}
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
          })}
          scroll={{ x: 'max-content' }}
          bordered
        />
      </Card>

      {/* Update Modal */}
      <UpdateClassModal
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        classInfo={selectedClass}
        onUpdateSuccess={handleUpdateSuccess}
      />
    </div>
  );
}

export default Published;
